import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useGetPersonalizedFertilizerPdpQuery } from "../../shared/api/backendApi";
import {
  useAddItemsToCartMutation,
  useChangeLineMutation,
  useLazyGetCartQuery,
} from "../../shared/api/shopifyCartApi";
import { PdpNavigationSteps } from "../../shared/pdp/components/PdpNavigationSteps";
import {
  getCurrentNavigationStep,
  navigateToNextPdpStepOrCart,
  openCartDrawerIfReturningFromCheckout,
} from "../../shared/pdp/utils/navigationSteps";
import { toShopifyNumericId } from "../../shared/pdp/utils/shopifyIds";
import { LoadingSpinner } from "../../shared/pdp/components/LoadingSpinner";
import { PortableText } from "../../shared/pdp/components/PortableText";
import {
  areCartPropertiesEqual,
  getFertilizerCartProperties,
} from "../../shared/pdp/utils/fertilizerCartProperties";
import { getThemeAssetUrl } from "../../shared/pdp/utils/themeAssets";
import { PersonalizedFertilizerPdpLayout } from "./components/PersonalizedFertilizerPdpLayout";
import { FertilizerPurchaseModal } from "./components/sections/FertilizerPurchaseModal";
import type { PersonalizedFertilizerPdpConfig } from "./types";
import { readPersonalizedFertilizerQueryString } from "./queryString";
import {
  type FertilizerPurchaseOption,
  getFertilizerPurchaseOption,
  getInitialFertilizerLawnSize,
  getSelectedFertilizerVariant,
} from "./purchaseOptions";
import "./styles.css";

type PersonalizedFertilizerPdpAppProps = {
  config: PersonalizedFertilizerPdpConfig;
};

const FERTILIZER_PRODUCT_ID = "15582615568710";
const LAB_SOIL_ANALYSIS_SKU = "1004";
const AI_SOIL_ANALYSIS_SKU = "vHalmBodenanalyseKI";
const coachMatiasImageUrl = getThemeAssetUrl("coach-matias.png");
const SOIL_ANALYSIS_SKUS = [LAB_SOIL_ANALYSIS_SKU, AI_SOIL_ANALYSIS_SKU];
const LAB_SOIL_ANALYSIS_CART_PROPERTIES = {
  _soil_test_timing: "before_products",
};

export function PersonalizedFertilizerPdpApp({
  config,
}: PersonalizedFertilizerPdpAppProps) {
  const queryString = useMemo(readPersonalizedFertilizerQueryString, []);
  const [selectedLawnSize, setSelectedLawnSize] = useState<number | null>(null);
  const [selectedSoilAnalysisType, setSelectedSoilAnalysisType] = useState<
    "AI" | "LAB"
  >("LAB");
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const {
    data: fertilizer,
    isError,
    isLoading,
  } = useGetPersonalizedFertilizerPdpQuery({ queryString });
  const [getCart, getCartState] = useLazyGetCartQuery();
  const [addItemsToCart, addItemsToCartState] = useAddItemsToCartMutation();
  const [changeLine, changeLineState] = useChangeLineMutation();

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        openCartDrawerIfReturningFromCheckout();
      }
    };

    openCartDrawerIfReturningFromCheckout();
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (!fertilizer || selectedLawnSize !== null) return;

    setSelectedLawnSize(getInitialFertilizerLawnSize(fertilizer));
  }, [fertilizer, selectedLawnSize]);

  const selectedVariant = fertilizer
    ? getSelectedFertilizerVariant(fertilizer.variants, selectedLawnSize)
    : null;
  const selectedSoilAnalysisVariant = fertilizer?.soilAnalysisVariants.find(
    (variant) => variant.type === selectedSoilAnalysisType,
  );
  const onetimePurchaseOption = selectedVariant
    ? getFertilizerPurchaseOption(selectedVariant, "ONETIME")
    : undefined;
  const soilAnalysisPrice = selectedSoilAnalysisVariant?.priceInCents ?? 0;
  const displayedFertilizerPrice = onetimePurchaseOption?.priceCents ?? 0;
  const displayedTotalPrice = displayedFertilizerPrice + soilAnalysisPrice;
  const hasRequiredPurchaseOptions = selectedVariant
    ? selectedVariant.purchaseOptions.some(
        (option) => option.purchaseType === "SUBSCRIPTION",
      ) &&
      selectedVariant.purchaseOptions.some(
        (option) => option.purchaseType === "ONETIME",
      )
    : false;

  const handleOpenPurchaseModal = () => {
    setStatusMessage("");
    setIsPurchaseModalOpen(true);
  };

  const handleAddToCart = async (
    selectedPurchaseOption: FertilizerPurchaseOption,
  ) => {
    if (!selectedVariant || !selectedSoilAnalysisVariant) return;

    setStatusMessage("");

    try {
      const selectedVariantNumericId = toShopifyNumericId(
        selectedPurchaseOption.variantId,
      );
      const selectedSoilAnalysisVariantNumericId = toShopifyNumericId(
        selectedSoilAnalysisVariant.variantId,
      );
      const selectedSoilAnalysisSku =
        selectedSoilAnalysisVariant.type === "LAB"
          ? LAB_SOIL_ANALYSIS_SKU
          : AI_SOIL_ANALYSIS_SKU;
      const soilAnalysisProperties =
        selectedSoilAnalysisVariant.type === "LAB"
          ? LAB_SOIL_ANALYSIS_CART_PROPERTIES
          : undefined;
      const fertilizerProperties = getFertilizerCartProperties(
        selectedPurchaseOption.sku,
        config.currentSeason,
      );
      const cart = await getCart().unwrap();
      const fertilizerItems = cart.items.filter(
        (item) => String(item.product_id) === FERTILIZER_PRODUCT_ID,
      );
      const matchingFertilizerItem = fertilizerItems.find(
        (item) =>
          item.id === selectedVariantNumericId &&
          areCartPropertiesEqual(item.properties, fertilizerProperties),
      );
      const soilAnalysisItems = cart.items.filter((item) =>
        SOIL_ANALYSIS_SKUS.includes(item.sku),
      );
      const matchingSoilAnalysisItem = soilAnalysisItems.find(
        (item) =>
          (item.sku === selectedSoilAnalysisSku ||
            item.id === selectedSoilAnalysisVariantNumericId) &&
          areCartPropertiesEqual(item.properties, soilAnalysisProperties),
      );

      const cartChangeOperations = [
        ...fertilizerItems
          .filter((item) => item.key !== matchingFertilizerItem?.key)
          .map((item) => ({ id: item.key, quantity: 0 })),
        ...(matchingFertilizerItem && matchingFertilizerItem.quantity > 1
          ? [{ id: matchingFertilizerItem.key, quantity: 1 }]
          : []),
        ...soilAnalysisItems
          .filter((item) => item.key !== matchingSoilAnalysisItem?.key)
          .map((item) => ({ id: item.key, quantity: 0 })),
        ...(matchingSoilAnalysisItem && matchingSoilAnalysisItem.quantity > 1
          ? [{ id: matchingSoilAnalysisItem.key, quantity: 1 }]
          : []),
      ];

      for (const operation of cartChangeOperations) {
        await changeLine(operation).unwrap();
      }

      const itemsToAdd = [
        ...(matchingFertilizerItem
          ? []
          : [
              {
                id: selectedVariantNumericId,
                quantity: 1,
                ...(selectedPurchaseOption.purchaseType === "SUBSCRIPTION"
                  ? { selling_plan: selectedPurchaseOption.sellingPlan }
                  : {}),
                ...(fertilizerProperties
                  ? { properties: fertilizerProperties }
                  : {}),
              },
            ]),
        ...(matchingSoilAnalysisItem
          ? []
          : [
              {
                id: selectedSoilAnalysisVariantNumericId,
                quantity: 1,
                ...(soilAnalysisProperties
                  ? { properties: soilAnalysisProperties }
                  : {}),
              },
            ]),
      ];

      let cartSections;
      if (itemsToAdd.length) {
        const addToCartResponse = await addItemsToCart({
          items: itemsToAdd,
          sections: ["cart-drawer", "cart-icon-bubble"],
          sections_url: window.location.pathname,
        }).unwrap();
        cartSections = addToCartResponse.sections;
      }

      // Release the modal's focus trap and inert background before opening the cart.
      flushSync(() => setIsPurchaseModalOpen(false));
      navigateToNextPdpStepOrCart(fertilizer?.navigationSteps, {
        cartSections,
      });
    } catch {
      setStatusMessage("Der Dünger konnte nicht hinzugefügt werden.");
    }
  };

  const handleSkip = () => {
    navigateToNextPdpStepOrCart(fertilizer?.navigationSteps);
  };

  const isSkipVisible =
    getCurrentNavigationStep(fertilizer?.navigationSteps)?.isSkippable === true;
  const isAddingToCart =
    addItemsToCartState.isLoading ||
    changeLineState.isLoading ||
    getCartState.isFetching;
  const timelinePortalTarget = useMemo(
    () =>
      document.querySelector<HTMLElement>("[data-fertilizer-timeline-root]"),
    [],
  );
  const reviewContentPortalTarget = useMemo(
    () =>
      document.querySelector<HTMLElement>(
        "[data-fertilizer-review-content-root]",
      ),
    [],
  );
  const objectionReasonsPortalTarget = useMemo(
    () =>
      document.querySelector<HTMLElement>(
        "[data-fertilizer-objection-reasons-root]",
      ),
    [],
  );

  return (
    <section
      className="react-pdp personalized-fertilizer-pdp"
      aria-labelledby="react-pdp-heading"
    >
      <div className="global-container">
        {isLoading ? (
          <div
            className="personalized-fertilizer-pdp__loading-screen"
            role="status"
            aria-live="polite"
            aria-label="Dünger wird geladen..."
          >
            <LoadingSpinner className="personalized-fertilizer-pdp__loading-spinner" />
          </div>
        ) : null}

        {isError ? (
          <div className="react-pdp__content">
            <p className="react-pdp__status">
              Der Dünger konnte nicht geladen werden.
            </p>
          </div>
        ) : null}

        {!isLoading &&
        !isError &&
        (!fertilizer ||
          !selectedVariant ||
          !selectedSoilAnalysisVariant ||
          !hasRequiredPurchaseOptions) ? (
          <div className="react-pdp__content">
            <p className="react-pdp__status">
              Aktuell ist kein passender Dünger verfügbar.
            </p>
          </div>
        ) : null}

        {!isLoading &&
        !isError &&
        fertilizer &&
        selectedVariant &&
        selectedSoilAnalysisVariant &&
        hasRequiredPurchaseOptions ? (
          <div className="react-pdp__page-header-and-content">
            <div className="react-pdp__page-header">
              <PdpNavigationSteps
                navigationSteps={fertilizer.navigationSteps}
              />
              <div
                className="react-pdp__onetime-heading-wrap"
                id="react-pdp-heading"
              >
                <PortableText
                  className="react-pdp__onetime-heading"
                  emphasisVariant="accent"
                  value={fertilizer.heading}
                />
                <div className="react-pdp__onetime-coach-image-wrapper">
                  <img
                    alt=""
                    className="react-pdp__onetime-coach-image"
                    src={coachMatiasImageUrl}
                  />
                </div>
              </div>
            </div>
            <PersonalizedFertilizerPdpLayout
              fertilizer={fertilizer}
              fertilizerCompareAtPrice={displayedFertilizerPrice}
              fertilizerPrice={displayedFertilizerPrice}
              isAddingToCart={isAddingToCart}
              isSkipVisible={isSkipVisible}
              onAddToCart={handleOpenPurchaseModal}
              onSkip={handleSkip}
              onSoilAnalysisTypeChange={setSelectedSoilAnalysisType}
              onLawnSizeChange={setSelectedLawnSize}
              reviewContentPortalTarget={reviewContentPortalTarget}
              selectedSoilAnalysisType={selectedSoilAnalysisType}
              selectedLawnSize={selectedVariant.lawnSize}
              objectionReasonsPortalTarget={objectionReasonsPortalTarget}
              statusMessage={statusMessage}
              timelinePortalTarget={timelinePortalTarget}
              totalCompareAtPrice={displayedTotalPrice}
              totalPrice={displayedTotalPrice}
            />
            <FertilizerPurchaseModal
              config={config}
              isAddingToCart={isAddingToCart}
              isOpen={isPurchaseModalOpen}
              onClose={() => setIsPurchaseModalOpen(false)}
              onConfirm={handleAddToCart}
              reviewContent={fertilizer.reviewContent}
              soilAnalysisPrice={soilAnalysisPrice}
              soilAnalysisType={selectedSoilAnalysisType}
              statusMessage={statusMessage}
              variant={selectedVariant}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}
